import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";
import { translate } from "@/i18n/translate";
import type {
  CatalogResponse,
  HistoryResponse,
  IngredientDraft,
  ProductDraft,
  RecipeDraft,
  RecipeVersionsResponse,
} from "./types";

/** Hand-written hooks of the catalog API (not in the committed OpenAPI schema). */

const BASE = "/api/admin/catalog";

export const catalogKeys = {
  all: ["catalog"] as const,
  catalog: ["catalog", "catalog"] as const,
  history: (limit: number) => ["catalog", "history", limit] as const,
  versions: (recipeId: string) => ["catalog", "versions", recipeId] as const,
};

export function useCatalog() {
  return useQuery({
    queryKey: catalogKeys.catalog,
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<CatalogResponse>(BASE, { signal });
      return data;
    },
    staleTime: 10_000,
    retry: 1,
  });
}

export function useCatalogHistory(limit = 100) {
  return useQuery({
    queryKey: catalogKeys.history(limit),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<HistoryResponse>(`${BASE}/history`, { params: { limit }, signal });
      return data;
    },
    staleTime: 10_000,
    retry: 1,
  });
}

export function useRecipeVersions(recipeId: string | null) {
  return useQuery({
    queryKey: catalogKeys.versions(recipeId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<RecipeVersionsResponse>(
        `${BASE}/recipes/${encodeURIComponent(recipeId ?? "")}/versions`,
        { signal },
      );
      return data;
    },
    enabled: !!recipeId,
    staleTime: 10_000,
    retry: 1,
  });
}

/** one write of the catalog API; every write answers with the whole catalog */
export interface CatalogWrite {
  method: "post" | "put";
  path: string;
  body: Record<string, unknown>;
}

const segment = (id: string) => encodeURIComponent(id);

export const catalogWrites = {
  createRecipe: (id: string, draft: RecipeDraft): CatalogWrite => ({
    method: "post",
    path: "/recipes",
    body: { id, ...draft },
  }),
  updateRecipe: (id: string, draft: RecipeDraft, baseVersion: number): CatalogWrite => ({
    method: "put",
    path: `/recipes/${segment(id)}`,
    body: { ...draft, base_version: baseVersion },
  }),
  createIngredient: (id: string, draft: IngredientDraft): CatalogWrite => ({
    method: "post",
    path: "/ingredients",
    body: { id, ...draft },
  }),
  updateIngredient: (id: string, draft: IngredientDraft, baseUpdatedAt: string | null | undefined): CatalogWrite => ({
    method: "put",
    path: `/ingredients/${segment(id)}`,
    body: { ...draft, base_updated_at: baseUpdatedAt ?? null },
  }),
  createProduct: (id: string, draft: ProductDraft): CatalogWrite => ({
    method: "post",
    path: "/products",
    body: { id, ...draft },
  }),
  updateProduct: (id: string, draft: ProductDraft, baseUpdatedAt: string | null | undefined): CatalogWrite => ({
    method: "put",
    path: `/products/${segment(id)}`,
    body: { ...draft, base_updated_at: baseUpdatedAt ?? null },
  }),
};

export function useCatalogWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ method, path, body }: CatalogWrite) => {
      const { data } = await axiosInstance.request<CatalogResponse>({ method, url: `${BASE}${path}`, data: body });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(catalogKeys.catalog, data);
      void queryClient.invalidateQueries({ queryKey: ["catalog", "history"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog", "versions"] });
    },
  });
}

export type WriteError =
  /** somebody changed it meanwhile (the edit names the version it was made on) */
  | { kind: "conflict"; message: string }
  /** the change would make the catalog inconsistent */
  | { kind: "invalid"; message: string; problems: string[] }
  | { kind: "other"; message: string };

type AxiosLike = {
  response?: { status?: number; data?: { error?: string; problems?: string[] } | string };
  message?: string;
};

export function classifyWriteError(error: unknown): WriteError {
  const e = error as AxiosLike;
  const status = e?.response?.status;
  const data = typeof e?.response?.data === "object" ? e.response.data : undefined;
  const message = data?.error ?? e?.message ?? translate("catalog.error.unknown");
  if (status === 409) return { kind: "conflict", message };
  if (status === 400 && data?.problems?.length) return { kind: "invalid", message, problems: data.problems };
  return { kind: "other", message };
}

export function errorMessage(error: unknown): string {
  const e = error as AxiosLike;
  const data = typeof e?.response?.data === "object" ? e.response.data : undefined;
  return data?.error ?? e?.message ?? translate("catalog.error.unknown");
}
